import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, CheckCircle, XCircle, DollarSign, Clock, AlertTriangle, History, Eye, CheckCircle2, FileText, User } from "lucide-react";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useRole } from "@/hooks/useRole";
import { AccessDenied } from "@/components/admin/games/AccessDenied";

interface PendingWithdrawal {
  withdrawal_id: string;
  mentor_id: string;
  mentor_name: string;
  mentor_email: string;
  amount_cents: number;
  transfer_type: 'pix' | 'bank_transfer';
  pix_key?: string | null;
  pix_key_type?: string | null;
  bank_name?: string | null;
  account_number?: string | null;
  agency?: string | null;
  holder_name?: string;
  requested_at: string;
}

interface ProcessedWithdrawal extends PendingWithdrawal {
  status: 'approved' | 'rejected';
  processed_at: string;
  admin_notes?: string;
  transaction_id?: string;
  approved_by?: string;
  approved_at?: string;
  approved_by_name?: string;
}

const SaquesAdmin = () => {
  console.log('🟢 [SaquesAdmin] Componente iniciado');
  
  const { role, loading: roleLoading } = useRole();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [pendingWithdrawals, setPendingWithdrawals] = useState<PendingWithdrawal[]>([]);
  const [processedWithdrawals, setProcessedWithdrawals] = useState<ProcessedWithdrawal[]>([]);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<PendingWithdrawal | null>(null);
  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [action, setAction] = useState<'approve' | 'reject'>('approve');

  useEffect(() => {
    console.log('🔍 [SaquesAdmin] Componente carregado, role:', role);
    if (role === 'admin') {
      console.log('🔍 [SaquesAdmin] Usuário é admin, carregando dados...');
      fetchPendingWithdrawals();
      fetchProcessedWithdrawals();
    }
  }, [role]);

  const fetchPendingWithdrawals = async () => {
    try {
      setLoading(true);
      console.log('🔍 [SaquesAdmin] Buscando saques pendentes...');

      const { data: withdrawals, error } = await supabase
        .rpc('get_pending_withdrawals_admin');

      if (error) {
        console.error('❌ [SaquesAdmin] Erro ao buscar saques:', error);
        
        // Fallback: tentar buscar diretamente da tabela
        console.log('🔄 [SaquesAdmin] Tentando fallback - busca direta da tabela...');
        const { data: directWithdrawals, error: directError } = await supabase
          .from('mentor_withdrawals')
          .select('*')
          .eq('status', 'pending')
          .order('requested_at', { ascending: true });
        
        if (directError) {
          console.error('❌ [SaquesAdmin] Erro no fallback:', directError);
          toast({
            title: 'Erro',
            description: `Erro ao carregar saques pendentes: ${error.message}`,
            variant: 'destructive',
          });
          return;
        }
        
        console.log('✅ [SaquesAdmin] Fallback bem-sucedido:', directWithdrawals?.length || 0);
        // Transformar dados do fallback
        const transformedWithdrawals = directWithdrawals?.map(w => ({
          withdrawal_id: w.id,
          mentor_id: w.mentor_profile_id,
          mentor_name: 'Carregando...',
          mentor_email: 'Carregando...',
          amount_cents: w.amount_cents,
          transfer_type: w.transfer_type || 'pix',
          pix_key: null,
          pix_key_type: null,
          bank_name: null,
          account_number: null,
          agency: null,
          holder_name: 'Carregando...',
          requested_at: w.requested_at
        })) || [];
        
        setPendingWithdrawals(transformedWithdrawals);
        return;
      }

      console.log('✅ [SaquesAdmin] Saques encontrados:', withdrawals?.length || 0);
      setPendingWithdrawals(withdrawals || []);

    } catch (error: any) {
      console.error('💥 [SaquesAdmin] Erro inesperado:', error);
      toast({
        title: 'Erro',
        description: 'Erro inesperado ao carregar dados. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessedWithdrawals = async () => {
    try {
      console.log('🔍 [SaquesAdmin] Buscando histórico de saques...');

      const { data: processedData, error } = await supabase
        .rpc('get_processed_withdrawals_admin');

      if (error) {
        console.error('❌ [SaquesAdmin] Erro ao buscar histórico:', error);
        
        // Fallback: buscar diretamente da tabela
        console.log('🔄 [SaquesAdmin] Tentando fallback para histórico...');
        const { data: directProcessed, error: directError } = await supabase
          .from('mentor_withdrawals')
          .select(`
            id,
            mentor_profile_id,
            amount_cents,
            status,
            requested_at,
            processed_at,
            transaction_id,
            transfer_type,
            admin_notes,
            approved_by,
            approved_at,
            profiles!mentor_profile_id(nickname, email)
          `)
          .in('status', ['approved', 'rejected'])
          .order('processed_at', { ascending: false });
        
        if (directError) {
          console.error('❌ [SaquesAdmin] Erro no fallback do histórico:', directError);
          toast({
            title: 'Aviso',
            description: 'Não foi possível carregar o histórico de saques.',
            variant: 'destructive',
          });
          return;
        }
        
        console.log('✅ [SaquesAdmin] Fallback do histórico bem-sucedido:', directProcessed?.length || 0);
        
        // Transformar dados do fallback
        const transformedProcessed = directProcessed?.map(w => ({
          withdrawal_id: w.id,
          mentor_id: w.mentor_profile_id,
          mentor_name: w.profiles?.nickname || 'Nome não encontrado',
          mentor_email: w.profiles?.email || 'Email não encontrado',
          amount_cents: w.amount_cents,
          transfer_type: w.transfer_type || 'pix',
          pix_key: null,
          pix_key_type: null,
          bank_name: null,
          account_number: null,
          agency: null,
          holder_name: 'Carregando...',
          requested_at: w.requested_at,
          status: w.status as 'approved' | 'rejected',
          processed_at: w.processed_at || '',
          admin_notes: w.admin_notes,
          transaction_id: w.transaction_id,
          approved_by: w.approved_by,
          approved_at: w.approved_at,
          approved_by_name: 'Admin'
        })) || [];
        
        setProcessedWithdrawals(transformedProcessed);
        return;
      }

      console.log('✅ [SaquesAdmin] Histórico encontrado:', processedData?.length || 0);
      
      // Transformar dados da função SQL
      const formattedProcessed = processedData?.map(item => ({
        withdrawal_id: item.withdrawal_id,
        mentor_id: item.mentor_id,
        mentor_name: item.mentor_name || 'Nome não encontrado',
        mentor_email: item.mentor_email || 'Email não encontrado',
        amount_cents: item.amount_cents,
        transfer_type: item.transfer_type || 'pix',
        pix_key: null,
        pix_key_type: null,
        bank_name: null,
        account_number: null,
        agency: null,
        holder_name: 'Dados não disponíveis',
        requested_at: item.requested_at,
        status: item.status as 'approved' | 'rejected',
        processed_at: item.processed_at || '',
        admin_notes: item.admin_notes,
        transaction_id: null,
        approved_by: null,
        approved_at: null,
        approved_by_name: item.approved_by_name || 'Sistema'
      })) || [];
      
      setProcessedWithdrawals(formattedProcessed);

    } catch (error: any) {
      console.error('💥 [SaquesAdmin] Erro inesperado no histórico:', error);
      toast({
        title: 'Aviso',
        description: 'Erro ao carregar histórico de saques.',
        variant: 'destructive',
      });
    }
  };

  const handleApproveWithdrawal = async (withdrawalId: string, notes?: string) => {
    try {
      setProcessing(true);
      console.log('✅ [SaquesAdmin] Aprovando saque:', withdrawalId);

      // Obter o token de autenticação
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Token de autenticação não encontrado');
      }

      // Chamar a Edge Function que aprova o saque E envia o email ao mentor
      const { data: result, error } = await supabase.functions.invoke('approve-withdrawal', {
        body: {
          withdrawalId,
          transactionId: undefined, // Pode ser preenchido pelo admin se necessário
          notes: notes || undefined
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('❌ [SaquesAdmin] Erro na Edge Function:', error);
        throw new Error(`Erro ao aprovar saque: ${error.message}`);
      }

      console.log('📊 [SaquesAdmin] Resultado da Edge Function:', result);

      if (!result?.success) {
        console.error('❌ [SaquesAdmin] Edge Function retornou erro:', result?.error);
        throw new Error(result?.error || 'Erro desconhecido ao aprovar saque');
      }

      console.log('✅ [SaquesAdmin] Saque aprovado com sucesso!');
      console.log('📧 [SaquesAdmin] Email de confirmação enviado ao mentor');
      
      toast({
        title: 'Sucesso',
        description: 'Saque aprovado com sucesso! O mentor receberá um email de confirmação.',
      });

      // Refresh the list
      await fetchPendingWithdrawals();
      await fetchProcessedWithdrawals();
      setSelectedWithdrawal(null);
      setAdminNotes('');

    } catch (error: any) {
      console.error('❌ [SaquesAdmin] Erro ao aprovar:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao aprovar saque. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectWithdrawal = async (withdrawalId: string, notes: string) => {
    if (!notes.trim()) {
      toast({
        title: 'Erro',
        description: 'É obrigatório informar o motivo da rejeição.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setProcessing(true);
      console.log('❌ [SaquesAdmin] Rejeitando saque:', withdrawalId);

      const { data, error } = await supabase
        .rpc('reject_withdrawal_admin', {
          withdrawal_id: withdrawalId,
          admin_notes: notes
        });

      if (error) {
        console.error('❌ [SaquesAdmin] Erro ao rejeitar saque:', error);
        throw error;
      }

      console.log('✅ [SaquesAdmin] Saque rejeitado com sucesso');
      
      toast({
        title: 'Sucesso',
        description: 'Saque rejeitado com sucesso.',
      });

      // Refresh the list
      await fetchPendingWithdrawals();
      await fetchProcessedWithdrawals();
      setSelectedWithdrawal(null);
      setAdminNotes('');

    } catch (error: any) {
      console.error('❌ [SaquesAdmin] Erro ao rejeitar:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao rejeitar saque. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const renderTransferType = (transferType: string) => {
    const icons = {
      pix: "🔐",
      ted: "🏦",
      doc: "📄"
    };
    
    const labels = {
      pix: "PIX",
      ted: "TED",
      doc: "DOC"
    };
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs">
        {icons[transferType as keyof typeof icons]} {labels[transferType as keyof typeof labels]}
      </span>
    );
  };

  const renderPixKey = (pixKey: string | null, pixKeyType: string | null) => {
    if (!pixKey || !pixKeyType) return null;
    
    const typeLabels = {
      cpf: "CPF",
      email: "E-mail",
      telefone: "Telefone",
      chave_aleatoria: "Chave Aleatória"
    };
    
    return (
      <div className="text-sm">
        <span className="text-gray-400">PIX ({typeLabels[pixKeyType as keyof typeof typeLabels]}): </span>
        <span className="text-white font-mono">
          {pixKeyType === 'cpf' ? pixKey.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : pixKey}
        </span>
      </div>
    );
  };

  const handleProcessWithdrawal = (withdrawal: PendingWithdrawal, actionType: 'approve' | 'reject') => {
    setSelectedWithdrawal(withdrawal);
    setAction(actionType);
    setAdminNotes("");
    setShowProcessDialog(true);
  };

  const confirmProcessWithdrawal = async () => {
    if (!selectedWithdrawal) return;
    
    try {
      setProcessing(true);
      
      // Para rejeição, observações são obrigatórias
      if (action === 'reject' && !adminNotes.trim()) {
        toast({
          title: 'Erro',
          description: 'Observações são obrigatórias para rejeição',
          variant: 'destructive',
        });
        return;
      }
      
      // ✅ SAQUE NORMAL - USAR FUNÇÃO DO BANCO
      // Obter ID do admin atual
      const { data: currentUser } = await supabase.auth.getUser();
      if (!currentUser?.user) {
        throw new Error('Usuário não autenticado');
      }
      
      const { data: result, error } = await supabase
        .rpc('admin_process_withdrawal', {
          p_withdrawal_id: selectedWithdrawal.withdrawal_id,
          p_admin_id: currentUser.user.id,
          p_action: action,
          p_notes: adminNotes.trim() || null,
          p_transaction_id: null
        });
      
      if (error || !result.success) {
        throw new Error(result?.error || 'Erro ao processar saque');
      }
      
      toast({
        title: 'Sucesso',
        description: action === 'approve' ? 
          'Saque aprovado com sucesso! O saldo do mentor foi reduzido.' : 
          'Saque rejeitado com sucesso',
      });
      
      // Atualizar lista
      await fetchPendingWithdrawals();
      await fetchProcessedWithdrawals();
      setShowProcessDialog(false);
      setSelectedWithdrawal(null);
      
    } catch (error: any) {
      console.error('Error processing withdrawal:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao processar solicitação',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f141c] pt-16">
        <Loader2 className="h-12 w-12 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  if (role !== "admin") {
    return <AccessDenied />;
  }

  if (loading) {
    return (
      <div className="bg-[#0f141c] min-h-screen pt-16 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0f141c] min-h-screen pt-16 p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Gerenciamento de Saques</h1>
            <p className="text-gray-400 text-sm mt-1">
              Aprovação e histórico de todas as solicitações de saque
            </p>
          </div>
          <Badge variant="outline" className="text-orange-400 border-orange-400">
            {pendingWithdrawals.length} pendentes
          </Badge>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardContent className="p-6">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-orange-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Saques Pendentes</p>
                  <p className="text-2xl font-bold text-white">
                    {pendingWithdrawals.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardContent className="p-6">
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 text-green-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Valor Pendente</p>
                  <p className="text-2xl font-bold text-white">
                    {formatCurrency(
                      pendingWithdrawals.reduce((sum, w) => sum + w.amount_cents, 0)
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardContent className="p-6">
              <div className="flex items-center">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Aprovados Hoje</p>
                  <p className="text-2xl font-bold text-white">
                    {processedWithdrawals.filter(w => 
                      w.status === 'approved' && 
                      new Date(w.processed_at).toDateString() === new Date().toDateString()
                    ).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardContent className="p-6">
              <div className="flex items-center">
                <History className="h-8 w-8 text-blue-400" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-400">Total Processados</p>
                  <p className="text-2xl font-bold text-white">
                    {processedWithdrawals.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs para Pendentes e Histórico */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="bg-[#242C37] w-full">
            <TabsTrigger value="pending" className="flex-1">
              Saques Pendentes
              {pendingWithdrawals.length > 0 && (
                <Badge className="ml-2 bg-orange-600 text-white">
                  {pendingWithdrawals.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1">
              Histórico de Saques
              {processedWithdrawals.length > 0 && (
                <Badge className="ml-2 bg-blue-600 text-white">
                  {processedWithdrawals.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="pending">
            {/* Lista de Saques Pendentes */}
            <Card className="bg-[#1E293B] border-[#2A3749]">
              <CardHeader>
                <CardTitle className="text-white">Saques Pendentes</CardTitle>
                <CardDescription className="text-gray-400">
                  Solicitações de saque aguardando aprovação
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingWithdrawals.length > 0 ? (
                  <div className="space-y-4">
                    {pendingWithdrawals.map((withdrawal) => (
                      <Card key={withdrawal.withdrawal_id} className="bg-[#242C37] border-[#2A3749]">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-4">
                                <div className="flex items-center gap-2">
                                  <User className="h-5 w-5 text-[#00B6F0]" />
                                  <div>
                                    <div className="font-semibold text-white">{withdrawal.mentor_name}</div>
                                    <div className="text-sm text-gray-400">{withdrawal.mentor_email}</div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <DollarSign className="h-5 w-5 text-green-400" />
                                  <span className="font-bold text-2xl text-green-400">
                                    {formatCurrency(withdrawal.amount_cents)}
                                  </span>
                                </div>
                                
                                <Badge className="bg-orange-600">
                                  {withdrawal.transfer_type === 'pix' ? 'PIX' : 'Transferência'}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-400">Solicitado em: </span>
                                  <span className="text-white">
                                    {format(new Date(withdrawal.requested_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                  </span>
                                </div>
                                
                                {withdrawal.transfer_type === 'pix' && withdrawal.pix_key && (
                                  <div>
                                    <span className="text-gray-400">PIX: </span>
                                    <span className="text-white font-mono">{withdrawal.pix_key}</span>
                                    <span className="text-gray-400 ml-2">
                                      ({withdrawal.pix_key_type?.toUpperCase()})
                                    </span>
                                  </div>
                                )}
                                
                                <div>
                                  <span className="text-gray-400">Titular: </span>
                                  <span className="text-white">{withdrawal.holder_name}</span>
                                </div>
                                
                                {withdrawal.bank_name && (
                                  <div>
                                    <span className="text-gray-400">Banco: </span>
                                    <span className="text-white">{withdrawal.bank_name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                onClick={() => setSelectedWithdrawal(withdrawal)}
                                className="bg-[#00B6F0] hover:bg-[#0099CC] text-white"
                                size="sm"
                              >
                                <FileText className="mr-2 h-4 w-4" />
                                Analisar
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <div className="text-4xl mb-4">🎉</div>
                    <p>Nenhum saque pendente</p>
                    <p className="text-sm mt-2">Todos os saques foram processados</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="history">
            {/* Lista de Saques Processados */}
            <Card className="bg-[#1E293B] border-[#2A3749]">
              <CardHeader>
                <CardTitle className="text-white">Histórico de Saques</CardTitle>
                <CardDescription className="text-gray-400">
                  Saques que já foram processados
                </CardDescription>
              </CardHeader>
              <CardContent>
                {processedWithdrawals.length > 0 ? (
                  <div className="space-y-4">
                    {processedWithdrawals.map((withdrawal) => (
                      <Card key={withdrawal.withdrawal_id} className="bg-[#242C37] border-[#2A3749]">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-4">
                                <div className="flex items-center gap-2">
                                  <User className="h-5 w-5 text-[#00B6F0]" />
                                  <div>
                                    <div className="font-semibold text-white">{withdrawal.mentor_name}</div>
                                    <div className="text-sm text-gray-400">{withdrawal.mentor_email}</div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <DollarSign className="h-5 w-5 text-green-400" />
                                  <span className="font-bold text-2xl text-green-400">
                                    {formatCurrency(withdrawal.amount_cents)}
                                  </span>
                                </div>
                                
                                <Badge className="bg-orange-600">
                                  {withdrawal.transfer_type === 'pix' ? 'PIX' : 'Transferência'}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-400">Solicitado em: </span>
                                  <span className="text-white">
                                    {format(new Date(withdrawal.requested_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                  </span>
                                </div>
                                
                                {withdrawal.transfer_type === 'pix' && withdrawal.pix_key && (
                                  <div>
                                    <span className="text-gray-400">PIX: </span>
                                    <span className="text-white font-mono">{withdrawal.pix_key}</span>
                                    <span className="text-gray-400 ml-2">
                                      ({withdrawal.pix_key_type?.toUpperCase()})
                                    </span>
                                  </div>
                                )}
                                
                                <div>
                                  <span className="text-gray-400">Titular: </span>
                                  <span className="text-white">{withdrawal.holder_name}</span>
                                </div>
                                
                                {withdrawal.bank_name && (
                                  <div>
                                    <span className="text-gray-400">Banco: </span>
                                    <span className="text-white">{withdrawal.bank_name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex flex-col gap-2 ml-4">
                              <Badge 
                                className={
                                  withdrawal.status === 'approved' ? 'bg-green-600' : 'bg-red-600'
                                }
                              >
                                {withdrawal.status === 'approved' ? '✅ Aprovado' : '❌ Rejeitado'}
                              </Badge>
                              
                              <div className="text-xs text-gray-400 text-center">
                                {withdrawal.processed_at && (
                                  <div>
                                    Processado em: {format(new Date(withdrawal.processed_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                                  </div>
                                )}
                                <div>Por: {withdrawal.approved_by_name}</div>
                                {withdrawal.admin_notes && (
                                  <div className="mt-2 p-2 bg-[#1E293B] rounded text-white text-xs">
                                    <strong>Observações:</strong> {withdrawal.admin_notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <div className="text-4xl mb-4">📋</div>
                    <p>Nenhum saque processado ainda</p>
                    <p className="text-sm mt-2">Saques aprovados ou rejeitados aparecerão aqui</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Modal de Análise */}
        {selectedWithdrawal && (
          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardHeader>
              <CardTitle className="text-white">Analisar Saque</CardTitle>
              <CardDescription className="text-gray-400">
                Aprovar ou rejeitar solicitação de saque
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#242C37] p-4 rounded-lg">
                <h3 className="font-semibold text-white mb-2">Detalhes do Saque</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Mentor: </span>
                    <span className="text-white">{selectedWithdrawal.mentor_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Valor: </span>
                    <span className="text-green-400 font-bold">
                      {formatCurrency(selectedWithdrawal.amount_cents)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Tipo: </span>
                    <span className="text-white">
                      {selectedWithdrawal.transfer_type === 'pix' ? 'PIX' : 'Transferência Bancária'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Data: </span>
                    <span className="text-white">
                      {format(new Date(selectedWithdrawal.requested_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Observações do Administrador (opcional)
                </label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Adicione observações sobre este saque..."
                  className="bg-[#242C37] border-[#2A3749] text-white"
                  rows={3}
                />
              </div>

              <div className="flex justify-between gap-4 pt-4">
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleApproveWithdrawal(selectedWithdrawal.withdrawal_id, adminNotes)}
                    disabled={processing}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {processing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                    )}
                    Aprovar
                  </Button>
                  
                  <Button
                    onClick={() => handleRejectWithdrawal(selectedWithdrawal.withdrawal_id, adminNotes)}
                    disabled={processing || !adminNotes.trim()}
                    variant="destructive"
                  >
                    {processing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <XCircle className="mr-2 h-4 w-4" />
                    )}
                    Rejeitar
                  </Button>
                </div>
                
                <Button
                  onClick={() => {
                    setSelectedWithdrawal(null);
                    setAdminNotes('');
                  }}
                  variant="outline"
                  className="border-[#2A3749] hover:bg-[#242C37] text-white"
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SaquesAdmin;
